import React, { useState, useEffect } from 'react';
import { dealerSalesRepsApi } from '@/services/api';
import { DealerSalesRep, CreateDealerSalesRepDto, UpdateDealerSalesRepDto } from '@/types';

interface UserFormModalProps {
  rep: DealerSalesRep | null;
  salesReps: DealerSalesRep[];
  onClose: () => void;
  onSave: () => void;
}

export function UserFormModal({ rep, salesReps, onClose, onSave }: UserFormModalProps) {
  const isEditing = !!rep;

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<'sales_rep' | 'sales_manager'>('sales_rep');
  const [reportsToId, setReportsToId] = useState('');
  const [commissionRateOverride, setCommissionRateOverride] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'terminated'>('active');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (rep) {
      setFirstName(rep.firstName);
      setLastName(rep.lastName);
      setEmail(rep.email);
      setPhone(rep.phone || '');
      setRole(rep.role);
      setReportsToId(rep.reportsToId || '');
      setCommissionRateOverride(rep.commissionRateOverride?.toString() || '');
      setHireDate(rep.hireDate || '');
      setStatus(rep.status);
    }
  }, [rep]);

  // Filter available managers (sales managers only, exclude self when editing)
  const availableManagers = salesReps.filter(
    (r) => r.role === 'sales_manager' && r.status === 'active' && (!rep || r.id !== rep.id)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!isEditing) {
      if (!password || password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    if (role === 'sales_rep' && !reportsToId) {
      setError('Sales reps must report to a sales manager');
      return;
    }

    try {
      setLoading(true);

      if (isEditing) {
        // Update existing rep
        const updates: UpdateDealerSalesRepDto = {
          firstName,
          lastName,
          email,
          phone: phone || undefined,
          role,
          reportsToId: reportsToId || undefined,
          commissionRateOverride: commissionRateOverride ? parseFloat(commissionRateOverride) : undefined,
          hireDate: hireDate || undefined,
          status,
        };

        await dealerSalesRepsApi.update(rep.id, updates);
      } else {
        // Create new rep
        const newRep: CreateDealerSalesRepDto = {
          firstName,
          lastName,
          email,
          phone: phone || undefined,
          password,
          role,
          reportsToId: reportsToId || undefined,
          commissionRateOverride: commissionRateOverride ? parseFloat(commissionRateOverride) : undefined,
          hireDate: hireDate || undefined,
        };

        await dealerSalesRepsApi.create(newRep);
      }

      onSave();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save team member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-keder-dark border border-keder-gray rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-white mb-4">
          {isEditing ? 'Edit Team Member' : 'Add Team Member'}
        </h3>

        {error && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-800 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="input-field"
                required
              />
            </div>
          </div>

          {/* Contact Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input-field"
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Password Fields (only when creating) */}
          {!isEditing && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  required
                  minLength={8}
                  placeholder="Min 8 characters"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field"
                  required
                  minLength={8}
                />
              </div>
            </div>
          )}

          {/* Role & Manager */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as any)}
                className="select-field"
                required
              >
                <option value="sales_rep">Sales Rep</option>
                <option value="sales_manager">Sales Manager</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Reports To {role === 'sales_rep' && <span className="text-red-500">*</span>}
              </label>
              <select
                value={reportsToId}
                onChange={(e) => setReportsToId(e.target.value)}
                className="select-field"
                required={role === 'sales_rep'}
                disabled={role === 'sales_manager'}
              >
                <option value="">
                  {role === 'sales_manager' ? 'None (Manager)' : 'Select a manager'}
                </option>
                {availableManagers.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.firstName} {manager.lastName}
                  </option>
                ))}
              </select>
              {role === 'sales_rep' && availableManagers.length === 0 && (
                <p className="text-xs text-yellow-500 mt-1">
                  No active managers available. Create a sales manager first.
                </p>
              )}
            </div>
          </div>

          {/* Commission & Hire Date */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Commission Rate Override (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={commissionRateOverride}
                onChange={(e) => setCommissionRateOverride(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                className="input-field"
                placeholder="Leave blank to use default"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to use default rate from settings
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Hire Date
              </label>
              <input
                type="date"
                value={hireDate}
                onChange={(e) => setHireDate(e.target.value)}
                className="input-field"
              />
            </div>
          </div>

          {/* Status (only when editing) */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
                className="select-field"
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-keder-gray">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
